import { DatasetCreateRequest } from '@/api/generated/models'
import {
  convertFilesToBase64 as utilConvertFilesToBase64,
  hasFiles as utilHasFiles,
} from '../../../../../../utils/fileConverter'

/**
 * DATA TRANSFORMATION UTILITIES
 *
 * This module handles transformations between frontend form data and backend API format.
 * Different entity types require different transformations and payloads.
 *
 * TRANSFORMATION FLOWS:
 * 1. Dataset (contextual/client/media) → DatasetCreateRequest
 * 2. Regional Dataset → RegionDatasetCreateRequest
 * 3. Form field names → API field names
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface RegionDatasetPayload {
  parent_id: number
  region_id: number
  file: string | { name: string; data: string; type: string }
}

export interface FileInfo {
  name: string
  blobFile: File | { name: string; isMock: boolean }
  fileKey?: number
}

// ============================================================================
// FIELD NAME TRANSFORMATIONS
// ============================================================================

/**
 * Transforms frontend field names to backend API field names
 *
 * Frontend uses some different conventions than the backend:
 * - markets (array of IDs) → market_ids
 * - aggregation → aggregation_method
 * - Nested media fields are grouped in media_parameters object
 */
export function transformFieldNames(
  formData: Partial<DatasetCreateRequest>,
): Partial<DatasetCreateRequest> {
  const transformed: any = { ...formData }

  // 1. Transform markets to market_ids if present
  if ('markets' in transformed && Array.isArray(transformed.markets)) {
    transformed.market_ids = transformed.markets.map((id: string | number) =>
      typeof id === 'string' ? parseInt(id, 10) : id,
    )
    delete transformed.markets
  }

  // 2. Transform aggregation to aggregation_method if present
  if ('aggregation' in transformed) {
    transformed.aggregation_method = transformed.aggregation
    delete transformed.aggregation
  }

  return transformed
}

// ============================================================================
// VALUE CLEANING
// ============================================================================

/**
 * Converts empty strings to undefined for optional fields
 *
 * React forms often use empty strings for empty inputs, but the backend
 * expects undefined/null for optional fields.
 */
export function cleanOptionalFields(
  data: Partial<DatasetCreateRequest>,
): Partial<DatasetCreateRequest> {
  const cleaned: any = { ...data }

  // Fields that should be undefined instead of empty string
  const optionalStringFields = [
    'client_id',
    'influence',
    'investment',
    'description',
    'source',
  ]

  optionalStringFields.forEach((field) => {
    if (cleaned[field] === '') {
      cleaned[field] = undefined
    }
  })

  // Markets should be undefined if empty array
  if (Array.isArray(cleaned.market_ids) && cleaned.market_ids.length === 0) {
    cleaned.market_ids = undefined
  }

  // Category should be null instead of empty string
  if (cleaned.category === '') {
    cleaned.category = null
  }

  // Handle NaN for optional number fields
  if (cleaned.media_parameters?.spend_constraint !== undefined) {
    if (isNaN(cleaned.media_parameters.spend_constraint)) {
      cleaned.media_parameters.spend_constraint = undefined
    }
  }

  return cleaned
}

// ============================================================================
// MEDIA PARAMETERS HANDLING
// ============================================================================

/**
 * Groups flat media channel fields into nested media_parameters object
 *
 * Frontend form has flat fields like media_channel_constraint,
 * but backend expects them nested in media_parameters.
 */
export function groupMediaParameters(data: any): Partial<DatasetCreateRequest> {
  const result: any = { ...data }

  // Only create media_parameters if this is a media channel dataset
  if (!result.part_of_media_channel) {
    return result
  }

  // Initialize media_parameters if not already present
  if (!result.media_parameters) {
    result.media_parameters = {}
  }

  // Map flat fields to media_parameters
  const mediaFieldMappings: Record<string, string> = {
    media_input_category: 'input_category',
    media_channel_lag: 'channel_lag',
    media_channel_constraint: 'spend_constraint',
    media_roi_assumption: 'roi_assumption',
  }

  Object.entries(mediaFieldMappings).forEach(([flatField, nestedField]) => {
    if (flatField in result) {
      result.media_parameters[nestedField] = result[flatField]
      delete result[flatField]
    }
  })

  // Ensure channel_constraint is mapped correctly
  if (result.media_parameters.spend_constraint !== undefined) {
    // Backend only accepts this for 'spend' input category
    if (result.media_parameters.input_category !== 'spend') {
      delete result.media_parameters.spend_constraint
    }
  }

  return result
}

// ============================================================================
// FILE HANDLING
// ============================================================================

/**
 * Attaches file to request data
 *
 * @param data - Request data
 * @param fileInfo - File info from upload section
 * @returns Data with file attached (null if no file)
 */
export function attachFile(
  data: Partial<DatasetCreateRequest>,
  fileInfo: FileInfo | null,
): Partial<DatasetCreateRequest> {
  const result: any = { ...data }

  // Remove any existing file field
  delete result.file

  if (!fileInfo) {
    result.file = null
    return result
  }

  // Extract actual File object from various possible structures
  let actualFile: File | null = null

  if (fileInfo.blobFile instanceof File) {
    actualFile = fileInfo.blobFile
  } else if ((fileInfo.blobFile as any)?.blobFile instanceof File) {
    actualFile = (fileInfo.blobFile as any).blobFile
  }

  result.file = actualFile || null

  return result
}

/**
 * Converts File objects to base64 for JSON transmission
 *
 * Re-exports the utility function from shared utils
 */
export const convertFilesToBase64 = utilConvertFilesToBase64

/**
 * Checks if data contains File objects
 *
 * Re-exports the utility function from shared utils
 */
export const hasFiles = utilHasFiles

// ============================================================================
// DATASET TRANSFORMATION
// ============================================================================

/**
 * Transforms form data to DatasetCreateRequest format
 *
 * This is the main transformation for regular datasets (contextual, client, media channel)
 *
 * @param formData - Raw form data
 * @param fileInfo - File upload info
 * @returns Transformed data ready for API
 */
export async function transformToDatasetRequest(
  formData: Partial<DatasetCreateRequest>,
  fileInfo: FileInfo | null,
): Promise<Partial<DatasetCreateRequest>> {
  // 1. Remove file from form data (we'll add it separately)
  const { file, ...cleanFormData } = formData as any

  // 2. Clean optional fields (empty string → undefined)
  let data = cleanOptionalFields(cleanFormData)

  // 3. Transform field names
  data = transformFieldNames(data)

  // 4. Group media parameters
  data = groupMediaParameters(data)

  // 5. Attach file
  data = attachFile(data, fileInfo)

  // 6. Convert files to base64 if present
  if (hasFiles(data)) {
    data = await convertFilesToBase64(data)
  }

  return data
}

// ============================================================================
// REGIONAL DATASET TRANSFORMATION
// ============================================================================

/**
 * Transforms form data to RegionDataset creation format
 *
 * Regional datasets require parent_id, region_id, and file
 *
 * @param parentDatasetId - Selected parent dataset ID (REQUIRED)
 * @param regionId - Selected region ID (REQUIRED)
 * @param fileInfo - File upload info (REQUIRED for segments)
 * @returns Transformed data for region dataset API
 */
export async function transformToRegionDatasetRequest(
  parentDatasetId: number | null,
  regionId: number,
  fileInfo: FileInfo | null,
): Promise<RegionDatasetPayload> {
  if (!parentDatasetId) {
    throw new Error('Parent dataset is required for regional dataset creation')
  }

  if (!fileInfo) {
    throw new Error('File is required for regional dataset creation')
  }

  // Extract actual File object
  let actualFile: File | null = null

  if (fileInfo.blobFile instanceof File) {
    actualFile = fileInfo.blobFile
  } else if ((fileInfo.blobFile as any)?.blobFile instanceof File) {
    actualFile = (fileInfo.blobFile as any).blobFile
  }

  if (!actualFile) {
    throw new Error('Valid file is required for regional dataset creation')
  }

  // Convert file to base64 using shared utility
  const fileData = await utilConvertFilesToBase64({ file: actualFile })

  return {
    parent_id: parentDatasetId,
    region_id: regionId,
    file: fileData.file,
  }
}

// ============================================================================
// EDIT MODE TRANSFORMATIONS
// ============================================================================

/**
 * Computes changed values for PATCH requests
 *
 * Only sends fields that have actually changed from the original values
 *
 * @param currentData - Current form data
 * @param originalData - Original data from initialData
 * @returns Object containing only changed fields
 */
export function getChangedValues(
  currentData: Partial<DatasetCreateRequest>,
  originalData: Partial<DatasetCreateRequest> | null,
): Partial<DatasetCreateRequest> {
  // If no original data, return all current data (shouldn't happen in edit mode)
  if (!originalData) {
    return currentData
  }

  const changes: any = {}

  // Compare each field
  Object.keys(currentData).forEach((key) => {
    const currentValue = (currentData as any)[key]
    const originalValue = (originalData as any)[key]

    // Handle arrays specially
    if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
      const arraysEqual =
        currentValue.length === originalValue.length &&
        currentValue.every((val, index) => val === originalValue[index])

      if (!arraysEqual) {
        changes[key] = currentValue
      }
    }
    // Handle nested objects
    else if (
      currentValue &&
      typeof currentValue === 'object' &&
      !Array.isArray(currentValue) &&
      originalValue &&
      typeof originalValue === 'object'
    ) {
      const nestedChanges = getChangedValues(currentValue, originalValue)
      if (Object.keys(nestedChanges).length > 0) {
        changes[key] = { ...originalValue, ...nestedChanges }
      }
    }
    // Handle primitives
    else if (currentValue !== originalValue) {
      changes[key] = currentValue
    }
  })

  return changes
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Extracts region ID from selected regions array
 *
 * @param selectedRegions - Array of selected region items
 * @returns Region ID as number, or null if none selected
 */
export function extractRegionId(
  selectedRegions: Array<{ id: string | number; name: string }>,
): number | null {
  if (!selectedRegions || selectedRegions.length === 0) {
    return null
  }

  const regionId = selectedRegions[0].id
  return typeof regionId === 'string' ? parseInt(regionId, 10) : regionId
}

/**
 * Validates that transformed data is ready for submission
 *
 * Performs sanity checks on the transformed data
 */
export function validateTransformedData(
  data: any,
  isSegment: boolean,
): string | null {
  if (isSegment) {
    // Regional dataset validation
    if (!data.region_id) {
      return 'Region ID is required for segments'
    }
    if (!data.file) {
      return 'File is required for segments'
    }
  } else {
    // Regular dataset validation
    if (!data.name) {
      return 'Dataset name is required'
    }
  }

  return null
}
